import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Flex,
  Tooltip,
  Divider,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Tabs,
  TabsList,
  Tab,
  TabPanel,
  Button,
  Link,
  Checkbox,
  Input,
  ModalFooter,
  Switch,
} from "@appsmith/ads";
import { useDispatch, useSelector } from "react-redux";
import { EditInteractionKind, SavingState } from "@appsmith/ads-old";
import styled from "styled-components";

import {
  APPLICATION_INVITE,
  COMMUNITY_TEMPLATES,
  createMessage,
  DEPLOY_BUTTON_TOOLTIP,
  DEPLOY_MENU_OPTION,
  IN_APP_EMBED_SETTING,
  INVITE_TAB,
  HEADER_TITLES,
} from "ee/constants/messages";
import EditorName from "pages/Editor/EditorName";
import {
  getCurrentApplicationId,
  getCurrentPageId,
  getIsPageSaving,
  getIsPublishingApplication,
  getPageById,
  getPageSavingError,
} from "selectors/editorSelectors";
import {
  getApplicationList,
  getCurrentApplication,
  getIsErroredSavingAppName,
  getIsSavingAppName,
} from "ee/selectors/applicationSelectors";
import {
  publishApplication,
  updateApplication,
} from "ee/actions/applicationActions";
import { getCurrentAppWorkspace } from "ee/selectors/selectedWorkspaceSelectors";
import { Omnibar } from "pages/Editor/commons/Omnibar";
import ToggleModeButton from "pages/Editor/ToggleModeButton";
import { EditorShareButton } from "pages/Editor/EditorShareButton";
import AppInviteUsersForm from "pages/workspace/AppInviteUsersForm";
import { getEmbedSnippetForm } from "ee/utils/BusinessFeatures/privateEmbedHelpers";
import CommunityTemplatesPublishInfo from "pages/Editor/CommunityTemplates/Modals/CommunityTemplatesPublishInfo";
import PublishCommunityTemplateModal from "pages/Editor/CommunityTemplates/Modals/PublishCommunityTemplate";
import DeployLinkButtonDialog from "components/designSystems/appsmith/header/DeployLinkButton";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";
import { getAppsmithConfigs } from "ee/configs";
import {
  getIsGitConnected,
  protectedModeSelector,
} from "selectors/gitSyncSelectors";
import { showConnectGitModal } from "actions/gitSyncActions";
import AnalyticsUtil from "ee/utils/AnalyticsUtil";
import type { NavigationSetting } from "constants/AppConstants";
import { useHref } from "pages/Editor/utils";
import { viewerURL } from "ee/RouteBuilder";
import HelpBar from "components/editorComponents/GlobalSearch/HelpBar";
import { EditorTitle } from "./EditorTitle";
import { useCurrentAppState } from "pages/Editor/IDE/hooks";
import { EditorState } from "ee/entities/IDE/constants";
import { EditorSaveIndicator } from "pages/Editor/EditorSaveIndicator";
import type { Page } from "ee/constants/ReduxActionConstants";
import { IDEHeader, IDEHeaderTitle } from "IDE";
import { APPLICATIONS_URL } from "constants/routes";
import { useNavigationMenuData } from "../../EditorName/useNavigationMenuData";
import { DEFAULT_CHAINS } from "hooks/useWagmi";
import { useMutation, useQuery } from "@tanstack/react-query";
import WalletConfigAPI from "api/WalletConfigAPI";

const StyledDivider = styled(Divider)`
  height: 50%;
`;

const { cloudHosting } = getAppsmithConfigs();

interface HeaderTitleProps {
  appState: EditorState;
  currentPage?: Page;
}

const HeaderTitleComponent = ({ appState, currentPage }: HeaderTitleProps) => {
  switch (appState) {
    case EditorState.DATA:
      return (
        <IDEHeaderTitle
          key={appState}
          title={createMessage(HEADER_TITLES.DATA)}
        />
      );
    case EditorState.EDITOR:
      return <EditorTitle key={appState} title={currentPage?.pageName || ""} />;
    case EditorState.SETTINGS:
      return (
        <IDEHeaderTitle
          key={appState}
          title={createMessage(HEADER_TITLES.SETTINGS)}
        />
      );
    case EditorState.LIBRARIES:
      return (
        <IDEHeaderTitle
          key={appState}
          title={createMessage(HEADER_TITLES.LIBRARIES)}
        />
      );
    default:
      return <EditorTitle key={appState} title={currentPage?.pageName || ""} />;
  }
};

const Header = () => {
  const dispatch = useDispatch();

  // selectors
  const currentWorkspace = useSelector(getCurrentAppWorkspace);
  const applicationId = useSelector(getCurrentApplicationId);
  const isSavingName = useSelector(getIsSavingAppName);
  const currentApplication = useSelector(getCurrentApplication);
  const isErroredSavingName = useSelector(getIsErroredSavingAppName);
  const applicationList = useSelector(getApplicationList);
  const isProtectedMode = useSelector(protectedModeSelector);
  const isPublishing = useSelector(getIsPublishingApplication);
  const isGitConnected = useSelector(getIsGitConnected);
  const pageId = useSelector(getCurrentPageId) as string;
  const currentPage = useSelector(getPageById(pageId));
  const appState = useCurrentAppState();
  const isSaving = useSelector(getIsPageSaving);
  const pageSaveError = useSelector(getPageSavingError);

  // states
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("invite");
  const [
    showPublishCommunityTemplateModal,
    setShowPublishCommunityTemplateModal,
  ] = useState<boolean>(false);

  // feature flags
  const isGACEnabled = useFeatureFlag(FEATURE_FLAG.license_gac_enabled);
  const isPublishAppToCommunityEnabled = useFeatureFlag(
    FEATURE_FLAG.release_show_publish_app_to_community_enabled,
  );
  const isPrivateEmbedEnabled = useFeatureFlag(
    FEATURE_FLAG.license_private_embeds_enabled,
  );

  const deployLink = useHref(viewerURL, {
    basePageId: currentPage?.basePageId,
  });

  const updateApplicationDispatch = (
    id: string,
    data: { name: string; currentApp: boolean },
  ) => {
    dispatch(updateApplication(id, data));
  };

  const handlePublish = useCallback(() => {
    if (applicationId) {
      dispatch(publishApplication(applicationId));

      const appName = currentApplication ? currentApplication.name : "";
      const pageCount = currentApplication?.pages?.length;
      const navigationSettingsWithPrefix: Record<
        string,
        NavigationSetting[keyof NavigationSetting]
      > = {};

      if (currentApplication?.applicationDetail?.navigationSetting) {
        const settingKeys = Object.keys(
          currentApplication.applicationDetail.navigationSetting,
        ) as Array<keyof NavigationSetting>;

        settingKeys.map((key: keyof NavigationSetting) => {
          if (currentApplication?.applicationDetail?.navigationSetting?.[key]) {
            const value: NavigationSetting[keyof NavigationSetting] =
              currentApplication.applicationDetail.navigationSetting[key];

            navigationSettingsWithPrefix[`navigationSetting_${key}`] = value;
          }
        });
      }

      AnalyticsUtil.logEvent("PUBLISH_APP", {
        appId: applicationId,
        appName,
        pageCount,
        ...navigationSettingsWithPrefix,
        isPublic: !!currentApplication?.isPublic,
        templateTitle: currentApplication?.forkedFromTemplateTitle,
      });
    }
  }, [applicationId, currentApplication, dispatch]);

  const handleClickDeploy = useCallback(() => {
    if (isGitConnected) {
      dispatch(showConnectGitModal());
      AnalyticsUtil.logEvent("GS_DEPLOY_GIT_CLICK", {
        source: "Deploy button",
      });
    } else {
      handlePublish();
    }
  }, [dispatch, handlePublish, isGitConnected]);

  // wallet connect config
  const [showWalletConnectConfigModal, setShowWalletConnectConfigModal] =
    useState<boolean>(false);
  const [searchChainInput, setSearchChainInput] = useState("");
  const [showSelectedChains, setShowSelectedChains] = useState(false);
  const [selectedChainIds, setSelectedChainIds] = useState<number[]>(
    DEFAULT_CHAINS.map((chain) => chain.id),
  );
  const { data: supportedChains, refetch } = useQuery({
    queryKey: ["supportedChains", applicationId],
    queryFn: () => WalletConfigAPI.getSupportedChains(applicationId),
    enabled: !!applicationId,
  });

  useEffect(() => {
    setSelectedChainIds(
      !!supportedChains?.length
        ? supportedChains
        : DEFAULT_CHAINS.map((chain) => chain.id),
    );
  }, [supportedChains]);

  const supportedChainsMutation = useMutation({
    mutationFn: WalletConfigAPI.updateSupportedChains,
    onSuccess: () => {
      refetch();
      setShowWalletConnectConfigModal(false);
    },
  });

  const displayedChains = useMemo(() => {
    if (!searchChainInput)
      return showSelectedChains
        ? selectedChainIds.map((id) =>
            DEFAULT_CHAINS.find((chain) => chain.id === id),
          )
        : DEFAULT_CHAINS;

    return DEFAULT_CHAINS.filter(({ name, id }) => {
      const includesStr =
        name.toLowerCase().includes(searchChainInput.toLowerCase()) ||
        id.toString().toLowerCase().includes(searchChainInput.toLowerCase());
      if (showSelectedChains)
        return includesStr && selectedChainIds.includes(id);
      return includesStr;
    });
  }, [searchChainInput, showSelectedChains, selectedChainIds]);

  useEffect(() => {
    setSearchChainInput("");
    setShowSelectedChains(false);
  }, [showWalletConnectConfigModal]);

  return (
    <>
      <IDEHeader>
        <IDEHeader.Left>
          <HeaderTitleComponent appState={appState} currentPage={currentPage} />
          <EditorSaveIndicator isSaving={isSaving} saveError={pageSaveError} />
        </IDEHeader.Left>
        <IDEHeader.Center>
          <Flex alignItems={"center"}>
            {currentWorkspace.name && (
              <>
                <Link className="mr-1.5" to={APPLICATIONS_URL}>
                  {currentWorkspace.name}
                </Link>
                {"/"}
                <EditorName
                  applicationId={applicationId}
                  className="t--application-name editable-application-name max-w-48"
                  defaultSavingState={
                    isSavingName ? SavingState.STARTED : SavingState.NOT_STARTED
                  }
                  defaultValue={currentApplication?.name || ""}
                  editInteractionKind={EditInteractionKind.SINGLE}
                  editorName="Application"
                  fill
                  getNavigationMenu={useNavigationMenuData}
                  isError={isErroredSavingName}
                  isNewEditor={
                    applicationList.filter((el) => el.id === applicationId)
                      .length > 0
                  }
                  isPopoverOpen={isPopoverOpen}
                  onBlur={(value: string) =>
                    updateApplicationDispatch(applicationId || "", {
                      name: value,
                      currentApp: true,
                    })
                  }
                  setIsPopoverOpen={setIsPopoverOpen}
                />
              </>
            )}
          </Flex>
        </IDEHeader.Center>
        <IDEHeader.Right>
          <HelpBar />
          <StyledDivider orientation={"vertical"} />

          {/* wallet connect config */}
          {applicationId && (
            <Tooltip content="Custom wallet connect chains" placement="bottom">
              <Button
                className="t--application-edit-btn"
                kind="tertiary"
                onClick={() => setShowWalletConnectConfigModal(true)}
                size="md"
                startIcon="settings-line"
              >
                Custom Chains
              </Button>
            </Tooltip>
          )}
          <Modal
            onOpenChange={(isOpen) => setShowWalletConnectConfigModal(isOpen)}
            open={showWalletConnectConfigModal}
          >
            <ModalContent>
              <ModalHeader>Custom wallet connect chains</ModalHeader>
              <ModalBody>
                <div className="sticky left-0 top-0 pb-4 backdrop:blur backdrop:bg-white/80 z-10">
                  <div className="absolute left-0 -top-4 w-full h-4 bg-transparent z-10"></div>
                  <div className="flex items-center justify-between gap-6">
                    <Input
                      className="flex-1"
                      placeholder="Search chains by name or id"
                      startIcon="search-line"
                      size="md"
                      value={searchChainInput}
                      onChange={(value) => setSearchChainInput(value.trim())}
                    />
                    <div className="flex items-center w-fit shrink-0 gap-2 whitespace-nowrap">
                      <Switch
                        isSelected={showSelectedChains}
                        onChange={setShowSelectedChains}
                      />
                      <span>Show selected</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {displayedChains.map((chain) => (
                    <Checkbox
                      isSelected={
                        !!chain?.id && selectedChainIds.includes(chain.id)
                      }
                      onChange={(isSelected) =>
                        !!chain?.id &&
                        setSelectedChainIds((prev) =>
                          isSelected
                            ? [...prev, chain.id]
                            : prev.filter((id) => id !== chain.id),
                        )
                      }
                    >
                      {chain?.name} ({chain?.id})
                    </Checkbox>
                  ))}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  size="md"
                  isLoading={supportedChainsMutation.isPending}
                  onClick={() =>
                    supportedChainsMutation.mutateAsync({
                      applicationId,
                      supportedChains: selectedChainIds,
                    })
                  }
                  disabled={
                    !applicationId ||
                    !!!selectedChainIds.length ||
                    supportedChainsMutation.isPending
                  }
                >
                  Save
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {applicationId && <EditorShareButton setShowModal={setShowModal} />}
          <Modal
            onOpenChange={(isOpen) => setShowModal(isOpen)}
            open={showModal}
          >
            <ModalContent style={{ width: "640px" }}>
              <ModalHeader>
                {createMessage(
                  APPLICATION_INVITE,
                  currentWorkspace.name,
                  !isGACEnabled,
                )}
              </ModalHeader>
              <ModalBody>
                <Tabs
                  onValueChange={(value) => setActiveTab(value)}
                  value={activeTab}
                >
                  <TabsList>
                    <Tab data-testid="t--tab-INVITE" value="invite">
                      {createMessage(INVITE_TAB)}
                    </Tab>
                    <Tab data-testid="t--tab-EMBED" value="embed">
                      {createMessage(IN_APP_EMBED_SETTING.embed)}
                    </Tab>
                    {isPublishAppToCommunityEnabled && cloudHosting && (
                      <Tab data-testid="t--tab-PUBLISH" value="publish">
                        {createMessage(COMMUNITY_TEMPLATES.tabTitle)}
                      </Tab>
                    )}
                  </TabsList>
                  <TabPanel value="invite">
                    <AppInviteUsersForm
                      applicationId={applicationId}
                      workspaceId={currentWorkspace.id}
                    />
                  </TabPanel>
                  <TabPanel value="embed">
                    {getEmbedSnippetForm(isPrivateEmbedEnabled, setActiveTab)}
                  </TabPanel>
                  {cloudHosting && (
                    <TabPanel value="publish">
                      <CommunityTemplatesPublishInfo
                        onPublishClick={() =>
                          setShowPublishCommunityTemplateModal(true)
                        }
                        setShowHostModal={setShowModal}
                      />
                    </TabPanel>
                  )}
                </Tabs>
              </ModalBody>
            </ModalContent>
          </Modal>

          <PublishCommunityTemplateModal
            onPublishSuccess={() => {
              setShowPublishCommunityTemplateModal(false);
              setShowModal(true);
            }}
            setShowModal={setShowPublishCommunityTemplateModal}
            showModal={showPublishCommunityTemplateModal}
          />
          <div className="flex items-center gap-4">
            <Tooltip
              content={createMessage(DEPLOY_BUTTON_TOOLTIP)}
              placement="bottomRight"
            >
              <Button
                className="t--application-publish-btn"
                data-guided-tour-iid="deploy"
                id={"application-publish-btn"}
                isDisabled={isProtectedMode}
                isLoading={isPublishing}
                kind="tertiary"
                onClick={handleClickDeploy}
                size="md"
                // startIcon={"rocket"}
              >
                <div className="mr-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_660_7104)">
                      <path
                        d="M18.2913 7.50007C18.085 5.44435 17.1224 3.53858 15.5902 2.15254C14.0581 0.766502 12.0657 -0.000976562 9.99967 -0.000976562C7.93363 -0.000976562 5.94125 0.766502 4.40912 2.15254C2.87698 3.53858 1.91435 5.44435 1.70801 7.50007H1.91467L5.38301 12.5134L6.06884 12.0384L2.92801 7.50007H4.89634C4.89634 7.2238 5.00609 6.95885 5.20144 6.7635C5.39679 6.56815 5.66174 6.4584 5.93801 6.4584C6.21427 6.4584 6.47923 6.56815 6.67458 6.7635C6.86993 6.95885 6.97967 7.2238 6.97967 7.50007H7.33134L8.37051 10.8334L9.16634 10.5851L8.20384 7.50007H8.95884C8.95884 7.2238 9.06859 6.95885 9.26394 6.7635C9.45929 6.56815 9.72424 6.4584 10.0005 6.4584C10.2768 6.4584 10.5417 6.56815 10.7371 6.7635C10.9324 6.95885 11.0422 7.2238 11.0422 7.50007H11.7955L10.833 10.5851L11.6288 10.8334L12.668 7.50007H13.0213C13.0213 7.36327 13.0483 7.22782 13.1006 7.10144C13.153 6.97506 13.2297 6.86022 13.3264 6.7635C13.4232 6.66677 13.538 6.59004 13.6644 6.53769C13.7908 6.48534 13.9262 6.4584 14.063 6.4584C14.1998 6.4584 14.3353 6.48534 14.4616 6.53769C14.588 6.59004 14.7028 6.66677 14.7996 6.7635C14.8963 6.86022 14.973 6.97506 15.0254 7.10144C15.0777 7.22782 15.1047 7.36327 15.1047 7.50007H17.0572L13.9163 12.0392L14.6013 12.5134L18.0705 7.50007H18.2913ZM14.063 4.79173C13.6772 4.79307 13.2962 4.87732 12.9458 5.03878C12.5954 5.20025 12.2838 5.43515 12.0322 5.72757C11.7814 5.434 11.4699 5.1983 11.1192 5.03667C10.7686 4.87505 10.387 4.79136 10.0009 4.79136C9.61481 4.79136 9.23328 4.87505 8.88262 5.03667C8.53195 5.1983 8.22049 5.434 7.96967 5.72757C7.71282 5.42698 7.39247 5.18713 7.03172 5.02531C6.67097 4.86349 6.27881 4.78373 5.88351 4.79178C5.48821 4.79983 5.09962 4.89549 4.74576 5.07187C4.3919 5.24824 4.08158 5.50094 3.83717 5.81173C4.3358 4.58679 5.18817 3.53825 6.28547 2.79998C7.38276 2.06171 8.67519 1.66721 9.99772 1.66685C11.3203 1.6665 12.6129 2.0603 13.7106 2.79797C14.8083 3.53565 15.6612 4.58373 16.1605 5.8084C15.9094 5.49257 15.5906 5.23722 15.2275 5.06124C14.8645 4.88527 14.4665 4.79316 14.063 4.79173Z"
                        fill="white"
                      />
                      <path
                        d="M9.16659 12.8378L7.98826 11.667L4.97326 14.6628L3.79492 15.8337L7.98826 20.0003L9.16659 18.8295L6.15159 15.8337L9.16659 12.8378ZM10.8508 18.8295L12.0291 20.0003L15.0441 17.0045L16.2224 15.8337L12.0291 11.667L10.8508 12.8378L13.8658 15.8337L10.8508 18.8295Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_660_7104">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>

                {DEPLOY_MENU_OPTION()}
              </Button>
            </Tooltip>

            <DeployLinkButtonDialog link={deployLink} trigger="" />
            <ToggleModeButton />
          </div>
        </IDEHeader.Right>
      </IDEHeader>
      <Omnibar />
    </>
  );
};

export { Header };
